
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children }) => (
  <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: '28px 36px', marginBottom: 20, backdropFilter: 'blur(8px)' }}>
    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, marginBottom: 14, color: 'var(--t-primary)', letterSpacing: '-0.015em' }}>{title}</h2>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, marginBottom: 12, fontSize: '0.93rem' }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ color: 'var(--t-secondary)', lineHeight: 1.9, paddingLeft: '1.5rem', fontSize: '0.92rem', marginBottom: 12 }}>
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

export default function TermsOfService() {
  return (
    <div>
        <Helmet>
        <title>Terms of Service — ILoveConvert</title>
        <meta name="description" content="Terms of service for ILoveConvert free online file conversion tools. Read our usage policies, file ownership rights and disclaimers." />
        <link rel="canonical" href="https://iloveconvert.in/terms" />
        <meta property="og:title" content="Terms of Service — ILoveConvert" />
        <meta property="og:description" content="Terms of service for ILoveConvert free online file conversion tools." />
        <meta property="og:url" content="https://iloveconvert.in/terms" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms of Service — ILoveConvert" />
      </Helmet>
      
      <section className="hero" style={{ padding: '56px 28px 40px' }}>
        <div className="hero-inner">
          <h1 style={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)' }}>Terms of <span className="accent">Service</span></h1>
          <p style={{ color: 'var(--t-secondary)' }}>Last updated: July 2026</p>
        </div>
      </section>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 28px 80px' }}>

        <Section title="1. Acceptance of Terms">
          <P>By accessing or using ILoveConvert at iloveconvert.in (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.</P>
          <P>We reserve the right to update these Terms at any time. Your continued use of the Service after any changes constitutes your acceptance of the new Terms.</P>
        </Section>

        <Section title="2. Description of Service">
          <P>ILoveConvert provides free online file conversion services including but not limited to:</P>
          <UL items={[
            'PDF conversion (to/from Word, Excel, JPG, and other formats)',
            'PDF tools (merge, split, compress, rotate)',
            'Image format conversion (JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, ICO)',
            'AI-powered image background removal',
            'Image to text extraction (OCR)',
            'Office document conversion (Word, Excel, PowerPoint to PDF)',
          ]} />
          <P>The Service is provided free of charge. We intend to support it in part through advertising via the Google AdSense program once enabled — see our <Link to="/privacy" style={{ color: 'var(--red)' }}>Privacy Policy</Link> for details.</P>
        </Section>

        <Section title="3. Acceptable Use">
          <P>You agree to use the Service only for lawful purposes. You must not use the Service to:</P>
          <UL items={[
            'Upload, convert, or distribute files that infringe any intellectual property rights',
            'Process files containing malware, viruses, or malicious code',
            'Upload illegal content including child sexual abuse material, content that incites violence, or content illegal under applicable law',
            'Attempt to circumvent, disable, or interfere with security features of the Service',
            'Use the Service for automated bulk conversion without prior written permission',
            'Scrape, crawl, or otherwise harvest data from the Service',
            'Impersonate any person or entity or misrepresent your affiliation with any person or entity',
          ]} />
        </Section>

        <Section title="4. File Uploads and Ownership">
          <P>You retain all ownership rights to files you upload to ILoveConvert. By uploading a file, you grant us a temporary, limited license to process that file solely for the purpose of performing the conversion or operation you requested.</P>
          <P>You are responsible for ensuring that:</P>
          <UL items={[
            'You have the legal right to upload and convert the files you submit',
            'Your files do not violate any third-party intellectual property rights',
            'Your files do not contain unlawful content',
          ]} />
          <P>We do not claim ownership of any files you upload. All files are automatically deleted from our servers within 2 hours as described in our <Link to="/privacy" style={{ color: 'var(--red)' }}>Privacy Policy</Link>.</P>
        </Section>

        <Section title="5. Service Availability">
          <P>We strive to maintain the Service available 24 hours a day, 7 days a week. However, we do not guarantee uninterrupted availability. The Service may be temporarily unavailable due to:</P>
          <UL items={[
            'Scheduled maintenance and updates',
            'Unexpected technical issues',
            'Events outside our reasonable control',
          ]} />
          <P>We reserve the right to modify, suspend, or discontinue any part of the Service at any time without prior notice.</P>
        </Section>

        <Section title="6. File Size and Usage Limits">
          <P>The current maximum file size per upload is 50 MB for standard conversions and 25 MB for background removal. These limits may change at any time. We reserve the right to limit usage to prevent abuse of the free Service.</P>
        </Section>

        <Section title="7. Disclaimer of Warranties">
          <P>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:</P>
          <UL items={[
            'The Service will be error-free or uninterrupted',
            'Conversion results will meet your specific requirements',
            'All file formats and features will be supported at all times',
            'The Service will be free of viruses or other harmful components',
          ]} />
          <P>We make reasonable efforts to provide accurate and high-quality conversions, but conversion quality depends on the input file and cannot be guaranteed.</P>
        </Section>

        <Section title="8. Limitation of Liability">
          <P>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ILOVECONVERT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.</P>
          <P>Our total liability to you for any claims arising from use of the Service shall not exceed the amount you have paid us in the 12 months preceding the claim (which, for free users, is zero).</P>
        </Section>

        <Section title="9. Third-Party Services">
          <P>We rely on a small number of third-party providers to operate the Service — for hosting, database storage, temporary file storage, analytics, and advertising. The full list, and what each one does, is set out in our <Link to="/privacy" style={{ color: 'var(--red)' }}>Privacy Policy</Link>.</P>
          <P>Advertising on ILoveConvert is intended to be served through Google AdSense once enabled, governed by Google's own terms and privacy policies. We are not responsible for Google's data collection or use practices. By using this Service, you acknowledge that once advertising is active, Google may collect data about your browsing for the purpose of serving relevant advertisements, as described in our Privacy Policy.</P>
        </Section>

        <Section title="10. Governing Law">
          <P>These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of India.</P>
        </Section>

        <Section title="11. Contact">
          <P>For questions about these Terms of Service, please contact us:</P>
          <P>📧 Email: <a href="mailto:support@iloveconvert.in" style={{ color: 'var(--red)' }}>support@iloveconvert.in</a><br />🌐 Website: iloveconvert.in</P>
        </Section>

        {/* <AdInline /> */}
      </div>
    </div>
  );
}
